import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ClipboardCheck,
  HardHat,
  HelpCircle,
  Layers,
  ListChecks,
  LogOut,
  Menu,
  Receipt,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  User as UserIcon,
  Vote,
  X,
} from "lucide-react";
import { hasAnyRole, useAuth } from "../auth";
import { NotificationsBell } from "./Notifications";
import { ElmChatbot } from "./ElmChatbot";
import { cx } from "../ui";

export function Layout() {
  const { state, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-sand-50">
      <header className="bg-brand-800 sticky top-0 z-30 border-b border-white/[0.07]">
        <div className="max-w-7xl mx-auto px-4 sm:px-7 h-[58px] flex items-center gap-1">
          {/* Brand */}
          <Link
            to="/construction"
            className="flex items-center gap-2.5 mr-3 no-underline hover:no-underline"
          >
            <div className="w-[34px] h-[34px] rounded-[9px] bg-white/[0.13] border border-white/[0.15] flex items-center justify-center font-display text-base text-white font-medium">
              E
            </div>
            <div className="leading-none">
              <span className="font-display text-[15px] font-medium text-white tracking-[0.01em] block">
                The Elm at Clark
              </span>
              <span className="text-[9.5px] text-white/40 tracking-[0.1em] uppercase block mt-0.5">
                Construction Portal
              </span>
            </div>
          </Link>

          {state.status === "authenticated" ? (
            <>
              <div className="hidden md:block w-px h-[22px] bg-white/[0.12] mx-2.5" />
              <nav className="hidden md:flex items-center gap-1">
                {state.me.ownerships.length > 0 && (
                  <>
                    <DesktopLink to="/projects" icon={<ListChecks size={15} />}>
                      Projects
                    </DesktopLink>
                    <DesktopLink to="/my/insurance" icon={<Shield size={15} />}>
                      Insurance
                    </DesktopLink>
                    <DesktopLink to="/my/compliance" icon={<Receipt size={15} />}>
                      Compliance
                    </DesktopLink>
                  </>
                )}
                {hasAnyRole(state.me, ["property_manager", "admin"]) && (
                  <DesktopLink to="/admin/buildings" icon={<Layers size={15} />}>
                    Admin
                  </DesktopLink>
                )}
                {hasAnyRole(state.me, ["property_manager", "admin"]) && (
                  <DesktopLink to="/management" icon={<ShieldCheck size={15} />}>
                    Queue
                  </DesktopLink>
                )}
                {hasAnyRole(state.me, ["board_member"]) && (
                  <DesktopLink to="/board" icon={<Vote size={15} />}>
                    Board
                  </DesktopLink>
                )}
                {hasAnyRole(state.me, [
                  "property_manager",
                  "admin",
                  "chief_engineer",
                ]) && (
                  <DesktopLink to="/inspect" icon={<ClipboardCheck size={15} />}>
                    Inspect
                  </DesktopLink>
                )}
                <DesktopLink to="/contractors" icon={<HardHat size={15} />}>
                  Contractors
                </DesktopLink>
                <DesktopLink to="/help" icon={<HelpCircle size={15} />}>
                  Help
                </DesktopLink>
              </nav>

              <div className="ml-auto flex items-center gap-1.5">
                <NotificationsBell />
                <Link
                  to="/projects?focus=search"
                  className="hidden sm:flex w-[34px] h-[34px] rounded-md items-center justify-center text-white/55 hover:bg-white/[0.09] hover:text-white transition-colors no-underline hover:no-underline"
                  aria-label="Search"
                >
                  <Search size={17} />
                </Link>
                <ProfileMenu
                  firstName={state.me.user.firstName}
                  lastName={state.me.user.lastName}
                  email={state.me.user.email}
                  roles={state.me.roles}
                  ownerships={state.me.ownerships}
                  onLogout={() => void logout()}
                />
                <button
                  className="md:hidden w-[34px] h-[34px] rounded-md flex items-center justify-center text-white/85 hover:bg-white/[0.09]"
                  onClick={() => setMobileOpen((v) => !v)}
                  aria-label={mobileOpen ? "Close menu" : "Open menu"}
                >
                  {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
              </div>
            </>
          ) : (
            <nav className="ml-auto flex items-center gap-2">
              <Link
                to="/help/contractor"
                className="hidden sm:inline text-[13px] text-white/65 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/[0.09] no-underline hover:no-underline transition-colors"
              >
                Contractor help
              </Link>
              <Link
                to="/login"
                className="text-[13px] text-white px-3 py-1.5 rounded-md hover:bg-white/[0.09] no-underline hover:no-underline transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="btn-on-dark btn-sm no-underline hover:no-underline"
              >
                Register
              </Link>
            </nav>
          )}
        </div>

        {/* Mobile drawer */}
        {state.status === "authenticated" && mobileOpen && (
          <div className="md:hidden border-t border-white/[0.07] bg-brand-900 animate-pop">
            <div className="px-5 py-3 flex flex-col gap-1">
              {state.me.ownerships.length > 0 && (
                <>
                  <MobileLink to="/projects" icon={<ListChecks size={18} />}>
                    Projects
                  </MobileLink>
                  <MobileLink to="/my/compliance" icon={<Receipt size={18} />}>
                    Compliance
                  </MobileLink>
                  <MobileLink to="/my/insurance" icon={<Shield size={18} />}>
                    Insurance
                  </MobileLink>
                </>
              )}
              {hasAnyRole(state.me, ["property_manager", "admin"]) && (
                <MobileLink to="/admin/buildings" icon={<Layers size={18} />}>
                  Admin
                </MobileLink>
              )}
              {hasAnyRole(state.me, ["property_manager", "admin"]) && (
                <MobileLink to="/management" icon={<ShieldCheck size={18} />}>
                  Management queue
                </MobileLink>
              )}
              {hasAnyRole(state.me, ["board_member"]) && (
                <MobileLink to="/board" icon={<Vote size={18} />}>
                  Board
                </MobileLink>
              )}
              {hasAnyRole(state.me, [
                "property_manager",
                "admin",
                "chief_engineer",
              ]) && (
                <MobileLink to="/inspect" icon={<ClipboardCheck size={18} />}>
                  Inspector
                </MobileLink>
              )}
              <MobileLink to="/contractors" icon={<HardHat size={18} />}>
                Contractors
              </MobileLink>
              <MobileLink to="/help" icon={<HelpCircle size={18} />}>
                Help
              </MobileLink>
              <div className="h-px bg-white/[0.07] my-2" />
              <MobileLink to="/me/profile" icon={<UserIcon size={18} />}>
                Profile
              </MobileLink>
              <MobileLink to="/me/settings" icon={<Settings size={18} />}>
                Settings
              </MobileLink>
              <button
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-white/85 hover:bg-white/[0.09]"
                onClick={() => void logout()}
              >
                <LogOut size={18} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <Outlet />
      </main>

      <ElmChatbot />
    </div>
  );
}

// ---------------------------------------------------------------------------
function DesktopLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={false}
      className={({ isActive }) =>
        cx(
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-normal no-underline hover:no-underline transition-colors",
          isActive
            ? "bg-white/[0.11] text-white font-medium"
            : "text-white/65 hover:text-white hover:bg-white/[0.08]",
        )
      }
    >
      <span className={cx("opacity-75 inline-flex")}>{icon}</span>
      {children}
    </NavLink>
  );
}

function MobileLink({
  to,
  icon,
  children,
}: {
  to: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={false}
      className={({ isActive }) =>
        cx(
          "flex items-center gap-3 px-3 py-2.5 rounded-md text-[14px] no-underline hover:no-underline",
          isActive
            ? "bg-white/[0.11] text-white"
            : "text-white/75 hover:text-white hover:bg-white/[0.08]",
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
function ProfileMenu({
  firstName,
  lastName,
  email,
  roles,
  ownerships,
  onLogout,
}: {
  firstName: string;
  lastName: string;
  email: string;
  roles: Array<{ code: string; buildingId: string; buildingName: string | null }>;
  ownerships: Array<{
    unitId: string;
    unitNumber: string;
    buildingId: string;
    buildingName: string;
  }>;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const initials = `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button
        className="w-8 h-8 rounded-full bg-brand-600 border-[1.5px] border-white/20 flex items-center justify-center text-[11px] font-semibold text-white tracking-[0.03em] hover:bg-brand-700 transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open profile menu"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-72 bg-white text-ink-700 border border-sand-200 rounded-lg shadow-pop p-1.5 z-40 animate-pop">
          <div className="px-3 py-2.5">
            <div className="font-semibold text-ink-800 text-sm">
              {firstName} {lastName}
            </div>
            <div className="text-xs text-ink-500 truncate">{email}</div>
            <ProfileMenuIdentitySummary
              roles={roles}
              ownerships={ownerships}
            />
          </div>
          <div className="h-px bg-sand-100 my-1" />
          <Link
            to="/me/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-ink-700 hover:bg-sand-50 no-underline hover:no-underline"
          >
            <UserIcon size={15} /> Profile
          </Link>
          <Link
            to="/me/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-ink-700 hover:bg-sand-50 no-underline hover:no-underline"
          >
            <Settings size={15} /> Settings
          </Link>
          <div className="h-px bg-sand-100 my-1" />
          <button
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 rounded-md text-[13px] text-ink-700 hover:bg-sand-50 text-left"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role + unit summary inside the profile dropdown.
//
// Renders one line per role (with its building name) and one line
// per active ownership ("Owner · Unit 1108 at The Elm at Clark"),
// so the user can see at a glance which buildings + units they
// have access to without opening the profile page.
//
// A user with several roles or several units gets one line each;
// the dropdown widens slightly to accommodate. Empty role / no
// ownership renders nothing — the existing email line is enough.
// ---------------------------------------------------------------------------
const ROLE_LABELS: Record<string, string> = {
  unit_owner: "Owner",
  board_member: "Board Member",
  property_manager: "Property Manager",
  chief_engineer: "Chief Engineer",
  admin: "Administrator",
  contractor_primary_contact: "Contractor Contact",
};

function ProfileMenuIdentitySummary({
  roles,
  ownerships,
}: {
  roles: Array<{ code: string; buildingId: string; buildingName: string | null }>;
  ownerships: Array<{
    unitId: string;
    unitNumber: string;
    buildingId: string;
    buildingName: string;
  }>;
}) {
  // Dedupe + label roles. A user with property_manager + admin in
  // the same building shows up as two lines so the distinction is
  // visible. unit_owner the role is hidden because it's redundant
  // with the ownerships block right below.
  const visibleRoles = roles.filter((r) => r.code !== "unit_owner");
  if (visibleRoles.length === 0 && ownerships.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-0.5">
      {visibleRoles.map((r, i) => (
        <div key={`role-${i}`} className="text-[11.5px] text-ink-500 truncate">
          <span className="text-ink-700">
            {ROLE_LABELS[r.code] ?? r.code.replaceAll("_", " ")}
          </span>
          {r.buildingName && (
            <span className="text-ink-400"> · {r.buildingName}</span>
          )}
        </div>
      ))}
      {ownerships.map((o) => (
        <div key={o.unitId} className="text-[11.5px] text-ink-500 truncate">
          <span className="text-ink-700">Owner</span>
          <span className="text-ink-400">
            {" · Unit "}
            {o.unitNumber} at {o.buildingName}
          </span>
        </div>
      ))}
    </div>
  );
}
